import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { fetchDatastreamVersions } from "features/dashboard/store/dashboard.thunk";
import { ContentCard } from "components/ContentCard";
import CenterLoading from "components/common/CenterLoading";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import ConfirmActivateModal from "components/Modals/ConfirmActivateModal";
import AiccBreadCrumb from "components/common/AiccBreadCrumb";
import RuleEngineActions from "../components/RuleEngineActions";
import ruleEngine from "services/apis/ruleEngine";
import {
  CRUD_RULE_ENGINE_TYPES,
  formatDateToMyFormat,
  getPathPrefix,
  nameCapitalize,
} from "utils/coreUtils";
import ToastUtils from "utils/ToastUtils";
import AutoRuleEditorIcon from "assets/svg/AutoRuleEditorIcon";
import "../styles/styles.scss";

const RuleEngineVersions = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { datastream, versions: datastreamVersions } = useSelector(
    (state) => state?.datastream?.selectedDS
  );
  const companyDetails = useSelector((state) => state?.company);
  const [searchRule, setSearchRule] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [alreadyActive, setAlreadyActive] = useState(false);
  const [versionInfo, setGetVersionInfo] = useState();
  const [selectedRow, setSelectedRow] = useState();
  const [defaultPage, setDefaultPage] = useState();

  useEffect(() => {
    setDefaultPage(parseInt(sessionStorage.getItem("versionPage") ?? "1"));
  }, []);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "14px",
        padding: "0.75rem",
        verticalAlign: "middle",
        color: "#004e5c",
        fontWeight: 800,
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        padding: "0.75rem",
        verticalAlign: "middle",
      },
    },
  };

  const columns = useMemo(
    () => [
      {
        name: t("versionsForm.columns.Version"),
        selector: (row) => {
          return (
            <OverlayTrigger
              placement="auto"
              overlay={(props) => (
                <Tooltip id="button-tooltip" {...props}>
                  {nameCapitalize(row.versionName)}
                </Tooltip>
              )}
            >
              <a
                className="version-name-text active"
                onClick={() => {
                  navigate(
                    getPathPrefix(
                      `selected-datastream/rule-engine/versions/${row.dsConfigVersionId}/rule?a=${row.isActive}`
                    )
                  );
                }}
              >
                {nameCapitalize(row.versionName)}
              </a>
            </OverlayTrigger>
          );
        },
        minWidth: "100px",
        className: "",
      },
      {
        name: t("versionsForm.columns.createdBy"),
        selector: (row) => nameCapitalize(row.createdBy),
        sortable: true,
        minWidth: "180px",
        center: true,
      },
      {
        name: t("versionsForm.columns.createdDate"),
        selector: (row) => formatDateToMyFormat(row.createdAt, "DD/MM/YYYY"),
        sortable: true,
        minWidth: "180px",
        center: true,
      },
      {
        name: t("versionsForm.columns.updatedBy"),
        selector: (row) =>
          !(row.updatedBy ?? row.createdBy)
            ? "N/A"
            : nameCapitalize(row.updatedBy ?? row.createdBy),
        minWidth: "180px",
        center: true,
      },
      {
        name: t("versionsForm.columns.changeDate"),
        selector: (row) =>
          !(row.updatedAt ?? row.createdAt)
            ? "N/A"
            : formatDateToMyFormat(
                row.updatedAt ?? row.createdAt,
                "DD/MM/YYYY"
              ),
        sortable: true,
        minWidth: "180px",
        center: true,
      },
      {
        name: t("versionsForm.columns.status"),
        cell: (row) => (
          <div className="switch mt-1">
            <input
              type="checkbox"
              id="status"
              name="status"
              checked={row.isActive}
              onChange={(e) => {
                subStatusHandler(row, e.target.checked);
              }}
            />
          </div>
        ),
        minWidth: "70px",
        center: true,
      },
      {
        name: t("versionsForm.columns.actions"),
        center: true,
        minWidth: "170px",
        cell: (row) => (
          <RuleEngineActions
            classes="version-actions"
            actionId={row.dsConfigVersionId}
            editPath={`selected-datastream/rule-engine/versions/${row.dsConfigVersionId}?t=${CRUD_RULE_ENGINE_TYPES.EDIT}`}
            viewPath={`selected-datastream/rule-engine/versions/${row.dsConfigVersionId}?t=${CRUD_RULE_ENGINE_TYPES.VIEW}`}
            deleteMessage={
              <>
                {t("versionsForm.delete")} <b>{row?.versionName}</b>{" "}
                {t("versionsForm.version")}
              </>
            }
            deleteId={row.dsConfigVersionId}
            deleteHandler={deleteHandler}
            canDelete={!row.isActive}
            additonalActions={[
              {
                action: (
                  <OverlayTrigger
                    placement="auto"
                    overlay={(props) => (
                      <Tooltip id="copy-tooltip" {...props}>
                        {t("versionsForm.tooltip.configAutoRules")}
                      </Tooltip>
                    )}
                  >
                    <span className="action-icon me-2">
                      <button
                        disabled={!row.isActive}
                        onClick={async () => {
                          navigate(
                            getPathPrefix(
                              `selected-datastream/rule-engine/versions/${row.dsConfigVersionId}/autorule`
                            ),
                            {
                              state: { versionName: row.versionName },
                            }
                          );
                        }}
                      >
                        <AutoRuleEditorIcon size={20} color="#004e5c" />
                      </button>
                    </span>
                  </OverlayTrigger>
                ),
                sequence: 0,
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  const searchdata = useMemo(() => {
    const foundedData = datastreamVersions?.filter((data) => {
      return searchRule.trim()
        ? data?.versionName.toLowerCase().includes(searchRule.toLowerCase())
        : true;
    });
    return foundedData;
  }, [datastreamVersions, searchRule]);

  const searchRuleEngine = useMemo(() => {
    return (
      <>
        <div className="form-group col">
          <input
            className="form-control"
            placeholder={t("versionsForm.searchVersion")}
            value={searchRule}
            onChange={(e) => {
              setSearchRule(e.target.value);
              if (e.target.value === "") {
                setResetPaginationToggle(!resetPaginationToggle);
              }
            }}
          />
        </div>
        <div className="col d-flex justify-content-end align-item-end mb-3 clear-btn-pad"></div>
      </>
    );
  }, [resetPaginationToggle, searchRule, t]);

  const subStatusHandler = async (row, isChecked, isOverrite) => {
    setGetVersionInfo(row);
    let createRequest = {
      dsConfigVersionId: row.dsConfigVersionId,
      companyId: parseInt(companyDetails?.companyId),
      isActive: isChecked,
      dsConfigId: datastream?.dataStreamConfigId,
      isDeleted: false,
    };
    if (typeof isOverrite === "boolean" && isOverrite) {
      createRequest["isOverrite"] = isOverrite;
    }

    try {
      const response =
        await ruleEngine.saveOrUpdateDSConfigVersion(createRequest);
      if (response.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
      } else {
        toast.success(t(`statusCodes.${response?.data?.code}`));
        dispatch(fetchDatastreamVersions(datastream?.dataStreamConfigId));
      }
    } catch (error) {
      if (error.response.data.isDSConfigVersionActiveAlready) {
        setAlreadyActive(error.response.data.isDSConfigVersionActiveAlready);
      }
    }
  };

  const deleteHandler = async (id) => {
    try {
      const response = await ruleEngine.deleteDSConfigVersionById({
        dsConfigVersionId: id,
      });
      // if (response.status === 200) {
      //   toast.success(t("versionsForm.errorMessage.ruleDeletedSuccess"));
      // } else {
      //   toast.error(t("versionsForm.errorMessage.ruleDeletedFailure"));
      // }
      if (response.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
      } else {
        toast.success(t(`statusCodes.${response?.data?.code}`));
      }
      dispatch(fetchDatastreamVersions(datastream?.dataStreamConfigId));
    } catch (error) {
      /* empty */
    }
  };

  // for checkboxes
  const Checkbox = React.forwardRef(({ onClick, ...rest }, ref) => {
    return (
      <>
        <div className="form-check form-check-sm form-check-custom">
          <input
            type="checkbox"
            className="form-check-input"
            // style={{ height: "20px", width: "20px" }}
            ref={ref}
            onClick={onClick}
            {...rest}
          />
        </div>
      </>
    );
  });

  Checkbox.displayName = "Checkbox";

  const handleCreateCopy = async () => {
    try {
      const response = await ruleEngine.copyDsConfigVersionIdWithRules({
        dsConfigVersionId: selectedRow.dsConfigVersionId,
      });
      if (response.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
      } else {
        toast.success(t(`statusCodes.${response?.data?.code}`));
      }
      dispatch(fetchDatastreamVersions(datastream?.dataStreamConfigId));
    } catch (error) {
      if (error?.response?.data?.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${error?.response?.data?.code}`));
      } else {
        toast.success(t(`statusCodes.${error?.response?.data?.code}`));
        dispatch(fetchDatastreamVersions(datastream?.dataStreamConfigId));
      }

      if (error.response.data.isDSConfigVersionActiveAlready) {
        setAlreadyActive(error.response.data.isDSConfigVersionActiveAlready);
      } else if (error.response.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${error.response.data.code}`));
      }
    }
  };

  return (
    <div className="list-rule-engin">
      <ConfirmActivateModal
        show={alreadyActive}
        onOk={() => {
          subStatusHandler(versionInfo, true, true);
          setAlreadyActive(false);
        }}
        onClose={() => {
          setAlreadyActive(false);
        }}
        messageTitles={t("versionsForm.modalErrorMessage")}
      />

      <AiccBreadCrumb
        homeLink={getPathPrefix("dashboard")}
        title={datastream?.customDataStreamName}
        items={t("ruleEngineList.breadCrumb.items", {
          returnObjects: true,
        })}
      />
      <ContentCard
        headerImgSrc="/assets/images/engine.svg"
        title={t("versionsForm.ruleEngine")}
        headerContent={
          <>
            <PrimaryButton
              title={t("versionsForm.createActiveVersion")}
              icon="fa-regular fa-square-plus"
              className="me-2"
              onClick={() => {
                navigate(
                  getPathPrefix() +
                    "selected-datastream/rule-engine/versions/create/"
                );
              }}
            />
            <PrimaryButton
              title={t("versionsForm.createCopy")}
              icon="fa-sharp fa-solid fa-copy"
              disabled={selectedRow ? false : true}
              onClick={() => {
                handleCreateCopy();
              }}
            />
          </>
        }
        bodyContent={
          <div className="table-responsive">
            <DataTable
              responsive
              persistTableHead
              keyField={"id"}
              progressComponent={<CenterLoading minHeight={"150px"} />}
              columns={columns}
              data={searchdata}
              paginationResetDefaultPage={resetPaginationToggle}
              pagination
              className="table table-modern"
              selectableRows
              striped
              selectableRowsSingle
              selectableRowSelected={(row) =>
                selectedRow?.dsConfigVersionId === row.dsConfigVersionId &&
                selectedRow?.dsConfigId === row.dsConfigId
              }
              paginationDefaultPage={defaultPage}
              onChangePage={(page) => {
                sessionStorage.setItem("versionPage", page);
              }}
              selectableRowsComponent={Checkbox} // Pass the Checkbox component only
              onSelectedRowsChange={(e) => {
                if (e.selectedCount <= 0) setSelectedRow(undefined);

                setSelectedRow(e.selectedRows[0]);
              }}
              customStyles={customStyles}
              subHeader
              subHeaderAlign="left"
              subHeaderWrap={true}
              subHeaderComponent={searchRuleEngine}
            />
          </div>
        }
      />
    </div>
  );
};

export default RuleEngineVersions;
